// The rung of the delivery-promise cascade that decided a shipment's deadline,
// as worker/src/services/delivery-promise.ts records it. Sources that came from
// a named field carry that name after a colon, so the merchant can see which of
// their own fields ShipPromise read.
const FIXED_LABELS: Record<string, string> = {
  none: "No deadline — bought on price alone",
  rule: "Rule override",
  shopify_native: "Shopify's own delivery estimate",
  shop_default: "Shop-wide delivery promise",
  line_item_property: "Line item property",
};

const PREFIX_LABELS: Record<string, (rest: string) => string> = {
  marketplace_sla: (rest) => `Marketplace SLA — ${rest}`,
  metafield: (rest) => `Metafield ${rest}`,
  attribute: (rest) => `Order attribute "${rest}"`,
  tag: (rest) => `Order tag "${rest}"`,
  skip: (rest) => `Not shipped — ${rest}`,
};

export function describePromiseSource(source: string): string {
  const fixed = FIXED_LABELS[source];
  if (fixed) return fixed;

  const separator = source.indexOf(":");
  if (separator > 0) {
    const describe = PREFIX_LABELS[source.slice(0, separator)];
    const rest = source.slice(separator + 1);
    if (describe && rest) return describe(rest);
  }

  // A source this build has never heard of still tells the merchant more than
  // an empty cell does.
  return source;
}

// A promise *kind* — the category of field a deadline came from, as
// worker/src/db/promise-engine.ts stores it in source_a_kind / source_b_kind.
// Distinct from describePromiseSource above, which formats the cascade's
// colon-joined "kind:field" string.
//
// Shared by DeliverySourcesCard and PromiseConflictsCard. It was a local const in
// the first of those, covering six kinds; a second copy in the second card is how
// one of them ends up showing `mcf_estimate` to a merchant.
export const PROMISE_KIND_LABELS: Record<string, string> = {
  marketplace_sla: "Marketplace SLA",
  shopify_native: "Shopify estimate",
  metafield: "Metafield",
  attribute: "Order attribute",
  // The delivery-sources scan reports line_item_property; the promise engine
  // reports line_item. Same thing, two spellings, both have to resolve.
  line_item_property: "Line item property",
  line_item: "Line item property",
  tag: "Order tag",
  rule_override: "Rule override",
  // Declared in the engine's FULFILLMENT_SERVICE_SOURCES and emitted by nothing
  // yet — its enrichment hook is an empty block. Mapped so turning it on cannot
  // leak a raw enum into a card.
  mcf_estimate: "Amazon MCF",
  buy_with_prime: "Buy with Prime",
  none: "No promise found",
};

export function describePromiseKind(kind: string): string {
  return PROMISE_KIND_LABELS[kind] ?? kind;
}
